import { createContext, useContext, useMemo } from 'react'
import type { ReactNode } from 'react'

import type { TranslateFn } from '../types'
import { createTranslator, type Translator } from '../utils/createTranslator'

const DEFAULT_LOCALE = 'en-US'

const normalizeLocale = (locale?: string): string => {
  if (!locale) return DEFAULT_LOCALE
  return locale.replace(/_/g, '-')
}

interface I18nContextValue {
  t?: TranslateFn
  locale: string
}

const I18nContext = createContext<I18nContextValue>({
  locale: DEFAULT_LOCALE,
})

export interface I18nProviderProps {
  children: ReactNode
  t?: TranslateFn
  locale?: string
}

export const I18nProvider = ({ children, t, locale }: I18nProviderProps) => {
  const normalizedLocale = useMemo(() => normalizeLocale(locale), [locale])
  const value = useMemo(
    () => ({ t, locale: normalizedLocale }),
    [t, normalizedLocale],
  )

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export const useI18n = (): {
  locale: string
  text: Translator
  t: TranslateFn | undefined
} => {
  const { t, locale } = useContext(I18nContext)
  const text = useMemo(() => createTranslator(t), [t])

  return {
    locale,
    text,
    t,
  }
}
